import { ReactElement, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { COLLAPSED_CHANGE_ENTRIES, parseChangeSummary } from '../changeSummary'
import ChangeSummaryItem from './ChangeSummaryItem'

interface Props {
  text: string
}

/**
 * What the agent changed in a turn ("Vorgenommene Änderungen", its
 * `changes` block): one entry per affected element with its changes. Long
 * lists show the first few entries and expand on demand, so the chat stays
 * short.
 */
const ChangeSummaryCard = ({ text }: Props): ReactElement | null => {
  const [expanded, setExpanded] = useState(false)
  const entries = useMemo(() => parseChangeSummary(text), [text])

  if (entries.length === 0) return null

  const hidden = entries.length - COLLAPSED_CHANGE_ENTRIES
  const shown = expanded || hidden <= 0 ? entries : entries.slice(0, COLLAPSED_CHANGE_ENTRIES)

  return (
    <Box
      sx={{ border: 1, borderColor: 'primary.main', borderRadius: 1, p: 1, my: 1 }}
      data-cy="ai-chat-change-summary"
    >
      <Typography variant="subtitle2" mb={0.5}>
        Vorgenommene Änderungen
      </Typography>
      <Stack spacing={0.75}>
        {shown.map((entry, index) => (
          <ChangeSummaryItem key={index} entry={entry} />
        ))}
      </Stack>
      {hidden > 0 && (
        <Button
          size="small"
          onClick={() => setExpanded(previous => !previous)}
          sx={{ mt: 0.5 }}
          data-cy="ai-chat-change-summary-toggle"
        >
          {expanded ? 'Weniger anzeigen' : `${hidden} weitere anzeigen`}
        </Button>
      )}
    </Box>
  )
}

export default ChangeSummaryCard
